import React, { useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import Page from 'layout/page'
import PageTitle from 'layout/page-title'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import Dropdown from 'react-bootstrap/Dropdown'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Popover from 'react-bootstrap/Popover'
import Tooltip from 'react-bootstrap/Tooltip'
import Spinner from 'react-bootstrap/Spinner'
import Modal from 'react-bootstrap/Modal'
import Datetime from 'react-datetime'
import moment from 'moment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCog, faLayerGroup } from '@fortawesome/free-solid-svg-icons'
import ModuleIconCard from 'reports/form/module-card-icon'
import { groupBy } from 'lodash'
import useSections from 'api/hooks/use-sections'
import useAnalyticsMonitoring from 'api/hooks/use-analytics-monitoring'
import useAnalyticsMonitoringTransactions from 'api/hooks/use-analytics-monitoring-transactions'
import useMonitoringHighRiskTransactions from 'api/hooks/use-monitoring-high-risk-transactions'
import QuickLookDrawer from 'transactions/drawer'
import AppBadge from 'shared/badges/app-badge'
import RiskBadge from 'shared/badges/risk-badge'
import TableFooter from 'shared/tables/table-footer'
import MonitoringReviewActions from './review-actions'
import ConfigureModal from './configure-modal'
import DuplicateFindingCard from './duplicate-card'
import EvidenceFindingCard from './evidence-card'
import DuplicateReviewModal from './duplicate-review'
import EvidenceReviewModal from './evidence-review'
import { nextReview } from './review/next'

const DATE_PRESETS = [
  { key: '7d', label: '7d', days: 7 },
  { key: '14d', label: '14d', days: 14 },
  { key: '30d', label: '30d', days: 30 },
  { key: '90d', label: '90d', days: 90 },
  { key: 'custom', label: 'Custom', days: null }
]

const CATCH_ALL_WORKFLOW = 'all'

const FILTER_ALL = 'all'
const FILTER_HIGH_RISK = 'high_risk'
const DEFAULT_RISK_THRESHOLD = 2.5

const REVIEW_STATUS_OPTIONS = ['All', 'Open', 'Ignored', 'Resolved']
const DEFAULT_REVIEW_STATUSES = ['Open']

const SORT_OPTIONS = [
  { key: 'date', defaultLabel: 'Date' },
  { key: 'amount', label: 'Amount' },
  { key: 'risk', label: 'Risk' },
  { key: 'match', label: 'Match rate' }
]

const TABULAR = { fontVariantNumeric: 'tabular-nums' }

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(value || 0)

const formatNumber = (value) =>
  new Intl.NumberFormat('en-US').format(value || 0)

const MONITORING_STYLES = `
  .btn-outline-light.text-dark {
    color: var(--bs-dark) !important;
  }
  .btn-outline-light.text-dark:hover {
    color: var(--bs-dark) !important;
  }

  .monitoring-card {
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-left: 3px solid var(--bs-danger);
    border-radius: 6px;
    background: #fff;
    transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
    cursor: pointer;
  }
  .monitoring-card.is-risk { border-left-color: var(--bs-warning); }
  .monitoring-card:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
    border-color: rgba(220, 38, 38, 0.25);
  }
  .monitoring-card.is-risk:hover {
    border-color: rgba(245, 158, 11, 0.35);
  }
  .monitoring-card:active { transform: translateY(0); }
  .monitoring-card.is-resolved {
    border-left-color: #6c757d;
  }
  .monitoring-card.is-resolved .monitoring-eyebrow {
    color: #495057;
  }
  .monitoring-card.is-resolved:hover {
    border-color: rgba(108, 117, 125, 0.35);
  }

  .monitoring-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    font-size: 16px;
    font-weight: 500;
    border-radius: 999px;
    line-height: 1.5;
    white-space: nowrap;
    color: #475569;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
  }
  .monitoring-pill.weight-high {
    color: #b91c1c;
    background: #fef2f2;
    border-color: #fecaca;
  }
  .monitoring-pill.weight-med {
    color: #b45309;
    background: #fffbeb;
    border-color: #fed7aa;
  }
  .monitoring-pill.weight-low {
    color: #475569;
    background: #f8fafc;
    border-color: #e2e8f0;
  }
  .monitoring-pill-action {
    cursor: pointer;
    border-style: solid;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.6);
  }
  .monitoring-pill-action:hover,
  .monitoring-pill-action:focus-visible {
    filter: brightness(0.97);
    text-decoration: underline;
  }
  .monitoring-pill.is-excluded {
    color: #94a3b8;
    border-color: #e2e8f0;
    border-style: dashed;
    cursor: help;
  }
  .monitoring-analytic-tooltip .tooltip-inner {
    max-width: 320px;
    text-align: left;
  }
  .monitoring-settings-toggle::after {
    display: none !important;
  }
  .monitoring-settings-toggle {
    padding: 4px 10px;
  }

  .monitoring-eyebrow {
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #b91c1c;
  }
  .monitoring-card.is-risk .monitoring-eyebrow { color: #b45309; }

  .monitoring-match {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 0.75rem 1.5rem;
  }
  .monitoring-match-name {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: #1e293b;
  }
  .monitoring-match-name.is-empty {
    font-weight: 500;
    color: #94a3b8;
  }
  .monitoring-match-meta {
    font-size: 14px;
    color: #64748b;
    white-space: nowrap;
  }
  .monitoring-match-arrow {
    align-self: center;
    font-size: 18px;
    color: #cbd5e1;
  }
  .monitoring-match-score {
    display: inline-flex;
    align-items: baseline;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 999px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #b91c1c;
    white-space: nowrap;
  }
  .monitoring-match-score strong {
    font-size: 16px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
  .monitoring-match-score span {
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .monitoring-risk-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #64748b;
    padding: 2px 6px;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    background: #f8fafc;
  }
`

const reviewSurfaceFor = (analytic) => {
  if (analytic.is_excluded) return null
  if (analytic.supports_duplicate_review) return 'duplicate'
  if (analytic.supports_evidence_review) return 'evidence'
  return null
}

function TransactionCard({ transaction, dateLabel, onSelect, onOpenReview }) {
  const analytics = transaction.analytics || []
  // Every analytic governs status (not just is_control), so an exclusion must cover all.
  const governingAnalytics = analytics.map((a) => ({ id: a.id, name: a.name }))
  const count = analytics.length
  const riskScore = Number(transaction.risk_score || 0)
  const review = transaction.review
  const reviewStatus = review?.status
  const sectionFields = transaction.section_fields
  const groups = transaction.groups || []
  const isHighRisk = groups.includes('High Risk')
  const isFlagged = groups.includes('Flagged')

  const reviewable = analytics
    .map((analytic) => ({ analytic, surface: reviewSurfaceFor(analytic) }))
    .filter((entry) => entry.surface)
  const primaryReview =
    reviewable.find((entry) => entry.surface === 'duplicate') || reviewable[0]
  const openCard = () => {
    if (primaryReview) {
      onOpenReview(transaction, primaryReview.analytic, primaryReview.surface)
    } else {
      onSelect(transaction.id)
    }
  }

  const isResolved = reviewStatus === 'Resolved'
  const isIgnored = reviewStatus === 'Ignored'
  const isExcluded = reviewStatus === 'Excluded'

  const eyebrow = isFlagged
    ? `${count} ${count === 1 ? 'finding' : 'findings'}`
    : 'High risk'

  const cardClass = [
    'monitoring-card',
    isHighRisk && !isFlagged ? 'is-risk' : '',
    (isResolved || isIgnored || isExcluded) ? 'is-resolved' : ''
  ].filter(Boolean).join(' ')

  return (
    <div
      className={`${cardClass} p-3 mb-2`}
      role="button"
      tabIndex={0}
      onClick={openCard}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          openCard()
        }
      }}
    >
      <div
        className="d-flex justify-content-between align-items-center pb-2 mb-2"
        style={{ borderBottom: '1px dashed #e2e8f0', marginLeft: '-1rem', marginRight: '-1rem', paddingLeft: '1rem', paddingRight: '1rem' }}
      >
        <div className="monitoring-eyebrow d-flex align-items-center gap-3">
          <span><span aria-hidden="true">⚑</span>&nbsp;{eyebrow}</span>
          {isHighRisk && !isFlagged && (
            <>
              <span style={{ fontWeight: 500 }}>Risk Score:</span>
              <RiskBadge count={riskScore} showCount={true} />
            </>
          )}
          {isResolved && (
            <AppBadge variant="secondary" className="fw-normal" style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
              Resolved{review?.reason ? ` · ${review.reason}` : ''}
            </AppBadge>
          )}
          {isIgnored && (
            <AppBadge variant="secondary" className="fw-normal" style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
              Ignored{review?.reason ? ` · ${review.reason}` : ''}
            </AppBadge>
          )}
          {isExcluded && (
            <AppBadge variant="secondary" className="fw-normal" style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
              Excluded{review?.reason ? ` · ${review.reason}` : ''}
            </AppBadge>
          )}
        </div>
        <MonitoringReviewActions
          transactionId={transaction.id}
          review={review}
          compact
          sectionId={transaction.section_id}
          sectionAbbreviation={transaction.section_abbreviation}
          vendorId={transaction.vendor_id}
          vendorName={transaction.vendor_name}
          employeeId={transaction.employee_id}
          employeeName={transaction.employee_full_name}
          controlAnalytics={governingAnalytics}
          highRisk={isHighRisk}
        />
      </div>

      <div>
        <div
          className="fw-bold text-dark text-truncate"
          style={{ fontSize: '15px', letterSpacing: '-0.01em' }}
        >
          {transaction.vendor_name || 'Unknown vendor'}
        </div>
        <div className="d-flex flex-wrap mt-1" style={{ fontSize: '14px', gap: '2rem' }}>
          <div>
            <div className="text-muted small">Amount</div>
            <div className="fw-bold text-dark" style={TABULAR}>{formatCurrency(transaction.line_amount)}</div>
          </div>
          {transaction.employee_full_name && (
            <div>
              <div className="text-muted small">Entered By</div>
              <div className="text-dark">{transaction.employee_full_name}</div>
            </div>
          )}
          <div>
            <div className="text-muted small">{dateLabel}</div>
            <div className="text-dark">{moment(transaction.transaction_on).format('MMM D, YYYY')}</div>
          </div>
          {sectionFields?.invoice_number && (
            <div>
              <div className="text-muted small">Invoice #</div>
              <div className="text-dark">{sectionFields.invoice_number}</div>
            </div>
          )}
          {sectionFields?.po_number && (
            <div>
              <div className="text-muted small">PO #</div>
              <div className="text-dark">{sectionFields.po_number}</div>
            </div>
          )}
          {sectionFields?.invoice_description && (
            <div>
              <div className="text-muted small">Invoice Description</div>
              <div className="text-dark">{sectionFields.invoice_description}</div>
            </div>
          )}
          {sectionFields?.description && (
            <div>
              <div className="text-muted small">Description</div>
              <div className="text-dark">{sectionFields.description}</div>
            </div>
          )}
          {sectionFields?.card_number && (
            <div>
              <div className="text-muted small">Card #</div>
              <div className="text-dark">{sectionFields.card_number}</div>
            </div>
          )}
          {sectionFields?.merchant_code && (
            <div>
              <div className="text-muted small">MCC</div>
              <div className="text-dark">{sectionFields.merchant_code}</div>
            </div>
          )}
        </div>
      </div>

      {count > 0 && (
        <div className="d-flex align-items-start gap-3 mt-3">
          <span className="text-muted small" style={{ whiteSpace: 'nowrap', paddingTop: '3px' }}>Analytics Flagged:</span>
          <div className="d-flex flex-wrap gap-1">
            {analytics.map((analytic) => {
              const classes = [
                'monitoring-pill',
                `weight-${analytic.weight || 'low'}`,
                analytic.is_excluded ? 'is-excluded' : ''
              ].filter(Boolean).join(' ')
              // Pills reach a second surface; stopPropagation keeps the card's click from firing too.
              const surface = reviewSurfaceFor(analytic)
              const pill = surface
                ? (
                    <span
                      key={analytic.id}
                      className={`${classes} monitoring-pill-action`}
                      role="button"
                      tabIndex={0}
                      // No `title`: the OverlayTrigger below would stack a native tooltip on its own.
                      aria-label={`Open review — ${analytic.name}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        onOpenReview(transaction, analytic, surface)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          e.stopPropagation()
                          onOpenReview(transaction, analytic, surface)
                        }
                      }}
                    >
                      {analytic.name}
                      <span aria-hidden="true" style={{ marginLeft: '6px', opacity: 0.75 }}>&rarr;</span>
                    </span>
                  )
                : (
                    <span
                      key={analytic.id}
                      className={classes}
                      style={analytic.details || analytic.is_excluded ? { cursor: 'help' } : undefined}
                    >
                      {analytic.name}
                    </span>
                  )
              const tooltipContent = analytic.is_excluded
                ? (
                    <div style={{ textAlign: 'left' }}>
                      {analytic.details && (
                        <div className="mb-2">{analytic.details}</div>
                      )}
                      <div
                        style={{
                          borderTop: '1px solid rgba(255,255,255,0.25)',
                          paddingTop: '6px',
                          marginTop: analytic.details ? '6px' : 0
                        }}
                      >
                        <div
                          className="fw-bold"
                          style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.7 }}
                        >
                          Excluded if
                        </div>
                        {(analytic.excluded_by || []).map((rule, i) => (
                          <div key={i}>
                            <span style={{ opacity: 0.7 }}>{rule.excludable_type}:</span>{' '}
                            {rule.excludable_name}
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                : analytic.details
              if (tooltipContent) {
                return (
                  <OverlayTrigger
                    key={analytic.id}
                    placement="top"
                    delay={{ show: 200, hide: 300 }}
                    overlay={
                      <Tooltip className="monitoring-analytic-tooltip">
                        {tooltipContent}
                      </Tooltip>
                    }
                  >
                    {pill}
                  </OverlayTrigger>
                )
              }
              return pill
            })}
          </div>
        </div>
      )}

    </div>
  )
}

function StatCard({ label, value, subtext, variant }) {
  const valueClass =
    {
      danger: 'text-danger',
      warning: 'text-warning',
      primary: 'text-primary',
      success: 'text-success'
    }[variant] || 'text-dark'

  return (
    <Card className="h-100 shadow-sm">
      <Card.Body>
        <div className="text-muted text-uppercase small fw-bold mb-2">
          {label}
        </div>
        <div className={`h3 mb-1 ${valueClass}`} style={TABULAR}>
          {value}
        </div>
        {subtext && <div className="text-muted small">{subtext}</div>}
      </Card.Body>
    </Card>
  )
}

function Monitoring() {
  const { workflowSlug } = useParams()
  const workflow = workflowSlug || CATCH_ALL_WORKFLOW
  const isCatchAll = workflow === CATCH_ALL_WORKFLOW
  // Risk is transaction-grain; a workflow reviews records, so only the catch-all ranks by it.
  const usesRisk = isCatchAll

  const { data: allSections, isSuccess: sectionsLoaded } = useSections({
    options: { refetchOnWindowFocus: false }
  })

  // Section.monitoring on the backend is the single source of truth for
  // which sections surface here. The sections jbuilder exposes
  // `is_monitoring` so we don't maintain a parallel hardcoded list.
  const sections = useMemo(
    () => (allSections || []).filter((s) => s.is_monitoring),
    [allSections]
  )

  const defaultRange = useMemo(
    () => ({
      start_date: moment().subtract(30, 'days').format('YYYY-MM-DD'),
      end_date: moment().format('YYYY-MM-DD')
    }),
    []
  )

  const [params, setParams] = useState({
    section_id: '',
    review_statuses: DEFAULT_REVIEW_STATUSES,
    risk_threshold: DEFAULT_RISK_THRESHOLD,
    // Workflow cards count the whole backlog, so a date window would undercount the list.
    ...(isCatchAll ? defaultRange : {})
  })
  // The server scopes by workflow, so a stale section_id can't widen the result.
  const scopedParams = useMemo(() => ({ ...params, workflow }), [params, workflow])
  // Local draft for the risk threshold input so a user typing 2.55 doesn't
  // trigger three refetches while they're mid-type. Commits to `params`
  // after 400ms of inactivity. Lives alongside `params` so it still
  // persists across dataset changes and resets on reload.
  const [riskThresholdDraft, setRiskThresholdDraft] = useState(DEFAULT_RISK_THRESHOLD)

  React.useEffect(() => {
    const t = setTimeout(() => {
      const parsed = parseFloat(riskThresholdDraft)
      if (!Number.isNaN(parsed) && parsed >= 0) {
        setParams((prev) =>
          prev.risk_threshold === parsed ? prev : { ...prev, risk_threshold: parsed }
        )
      }
    }, 400)
    return () => clearTimeout(t)
  }, [riskThresholdDraft])
  const [activePreset, setActivePreset] = useState(isCatchAll ? '30d' : null)

  const [sortBy, setSortBy] = useState('date')
  const [sortDir, setSortDir] = useState('desc')
  // Shaped for the shared TableFooter, which owns page and per-page together.
  const [pagination, setPagination] = useState({ page: 1, perPage: 25 })
  const { page, perPage } = pagination
  const setPage = (next) => setPagination((prev) => ({ ...prev, page: next }))

  const selectReviewStatus = (status) => {
    if (status === 'All') {
      setParams((prev) => ({
        ...prev,
        review_statuses: ['Open', 'Ignored', 'Resolved', 'Excluded']
      }))
    } else {
      setParams((prev) => ({
        ...prev,
        review_statuses: [status]
      }))
    }
  }

  const isAllReviewStatuses =
    (params.review_statuses || []).length === 4 &&
    ['Open', 'Ignored', 'Resolved', 'Excluded'].every((s) => params.review_statuses.includes(s))
  const [activeFilter, setActiveFilter] = useState(FILTER_ALL)
  const [drawerShow, setDrawerShow] = useState(false)
  const [configureModalShow, setConfigureModalShow] = useState(false)
  const [datasetModalShow, setDatasetModalShow] = useState(false)
  const [selectedTransactionId, setSelectedTransactionId] = useState(null)
  // { transaction, analytic, surface }
  const [activeReview, setActiveReview] = useState(null)

  const selectedSection = sections?.find((s) => s.id === params.section_id)

  // Catch-all only: a workflow spans its own datasets, and an AP default would hide
  // vendor findings.
  React.useEffect(() => {
    if (!isCatchAll || !sectionsLoaded || !sections?.length || params.section_id) return
    // Prefer AP/PC: the card's section_fields only render those, so VO/Vendor look empty.
    const preferred = sections.find((s) => ['AP', 'PC'].includes(s.abbreviation))
    setParams((prev) => ({ ...prev, section_id: (preferred || sections[0]).id }))
  }, [isCatchAll, sectionsLoaded, sections, params.section_id])

  const applyPreset = (preset) => {
    setActivePreset(preset.key)
    if (preset.days == null) {
      // Custom — leave current start/end in place so the user can edit them.
      return
    }
    const end = moment()
    const start = moment().subtract(preset.days, 'days')
    setParams((prev) => ({
      ...prev,
      start_date: start.format('YYYY-MM-DD'),
      end_date: end.format('YYYY-MM-DD')
    }))
  }

  const onDateChange = (field) => (e) => {
    setParams((prev) => ({ ...prev, [field]: e.target.value }))
  }

  const sortedParams = {
    ...scopedParams,
    sort_by: sortBy,
    sort_dir: sortDir,
    page,
    per_page: perPage
  }
  const { review_statuses: _rs, ...summaryParams } = scopedParams

  // Reset to page 1 whenever the filter/sort/section/date/search changes
  // so we don't get stuck on page N of a stale result set.
  React.useEffect(() => {
    setPage(1)
  }, [
    params.section_id,
    params.start_date,
    params.end_date,
    params.review_statuses,
    params.risk_threshold,
    sortBy,
    sortDir,
    activeFilter
  ])

  // Hold the catch-all until its default dataset lands: without section_id the server
  // scans every monitoring dataset. No monitoring datasets at all is cheap, so let it through.
  const awaitingDataset =
    isCatchAll && !params.section_id && (!sectionsLoaded || sections.length > 0)

  const { data, isLoading, isError } = useAnalyticsMonitoring({
    params: summaryParams,
    options: { enabled: !awaitingDataset }
  })

  // A dataset outside the workflow returns an empty list that reads as "clean".
  const workflowMeta = data?.workflow
  const datasetOptions = useMemo(() => {
    const allowed = workflowMeta?.section_ids
    // Absent: not loaded yet. []: dormant workflow, which must not offer every dataset.
    if (!allowed) return sections
    return sections.filter((s) => allowed.includes(s.id))
  }, [sections, workflowMeta])
  const {
    data: controlData,
    isLoading: isLoadingControl,
    isFetching: isFetchingControl
  } = useAnalyticsMonitoringTransactions({
    params: sortedParams,
    options: { enabled: !awaitingDataset }
  })
  const {
    data: highRiskData,
    isLoading: isLoadingHighRisk,
    isFetching: isFetchingHighRisk
  } = useMonitoringHighRiskTransactions({
    params: sortedParams,
    // Off where risk isn't shown — an unshown list is still a request.
    options: { enabled: usesRisk && !awaitingDataset }
  })

  const controlTransactions = controlData?.transactions || []
  const highRiskTransactions = highRiskData?.transactions || []
  const controlMeta = controlData?.meta || {}
  const highRiskMeta = highRiskData?.meta || {}
  const activeMeta =
    activeFilter === FILTER_HIGH_RISK ? highRiskMeta : controlMeta
  const totalPages = activeMeta.total_pages || 1
  const totalCount = activeMeta.total_count || 0

  // Tag each transaction with its source group(s) for display.
  const taggedTransactions = useMemo(() => {
    const controlIds = new Set(controlTransactions.map((t) => t.id))
    const highRiskIds = new Set(highRiskTransactions.map((t) => t.id))

    const merged = new Map()
    controlTransactions.forEach((t) => merged.set(t.id, { ...t, groups: ['Flagged'] }))
    highRiskTransactions.forEach((t) => {
      if (merged.has(t.id)) {
        merged.get(t.id).groups.push('High Risk')
      } else {
        merged.set(t.id, { ...t, groups: ['High Risk'] })
      }
    })
    return merged
  }, [controlTransactions, highRiskTransactions])

  const visibleTransactions = useMemo(() => {
    if (activeFilter === FILTER_HIGH_RISK) return highRiskTransactions.map((t) => taggedTransactions.get(t.id))
    return [...taggedTransactions.values()]
  }, [activeFilter, highRiskTransactions, taggedTransactions])

  const isLoadingVisible =
    awaitingDataset ||
    (activeFilter === FILTER_HIGH_RISK ? isLoadingHighRisk : isLoadingControl || isLoadingHighRisk)

  const isRefetching =
    !isLoadingVisible &&
    (activeFilter === FILTER_HIGH_RISK ? isFetchingHighRisk : isFetchingControl || isFetchingHighRisk)

  const openDrawer = (transactionId) => {
    setSelectedTransactionId(transactionId)
    setDrawerShow(true)
  }

  const openReview = (transaction, analytic, surface) => {
    setActiveReview({ transaction, analytic, surface })
  }

  // Advance to the next undecided finding; re-opening just closes.
  const onReviewDecided = (change) => {
    const next = change?.status === 'Open'
      ? null
      : nextReview(visibleTransactions, activeReview?.transaction.id, change?.transactionIds)
    setActiveReview(next)
  }

  // No placeholder crumb, or the title would flip once the meta lands.
  const crumbs = [{ name: 'Monitoring', url: 'monitoring' }]
  if (workflowMeta) crumbs.push({ name: workflowMeta.title, url: '' })

  const typeFilters = [
    { key: FILTER_ALL, label: 'All' },
    { key: FILTER_HIGH_RISK, label: 'High Risk' }
  ]

  const usesMatch = workflowMeta?.surface === 'compare'
  const sortOptions = SORT_OPTIONS.filter(
    (o) => (o.key !== 'risk' || usesRisk) && (o.key !== 'match' || usesMatch)
  )
  // Don't strand the sort on "Match rate" after leaving a compare workflow.
  React.useEffect(() => {
    if (workflowMeta && !usesMatch && sortBy === 'match') setSortBy('date')
  }, [workflowMeta, usesMatch, sortBy])

  const statColumns = usesRisk ? 5 : 4

  return (
    <Page title="Monitoring">
      <style dangerouslySetInnerHTML={{ __html: MONITORING_STYLES }} />
      <PageTitle breadcrumbs={crumbs} />

      <Card className="mt-4 mb-3 shadow-sm">
        <Card.Body className="d-flex flex-wrap align-items-center justify-content-between gap-3">
          {workflowMeta && (
            <div>
              <h4 className="mb-0">{workflowMeta.title}</h4>
              {workflowMeta.summary && (
                <div className="text-muted small mt-1">{workflowMeta.summary}</div>
              )}
            </div>
          )}
          {/* Workflows have no date window, fixed datasets, and analytics configured elsewhere. */}
          {isCatchAll && (
            <div className="d-flex flex-wrap align-items-center gap-2 ms-auto">
              <div className="d-flex gap-1">
                {DATE_PRESETS.map((preset) => {
                  const isActive = activePreset === preset.key
                  if (preset.key === 'custom') {
                    return (
                      <OverlayTrigger
                        key={preset.key}
                        trigger="click"
                        placement="bottom"
                        rootClose
                        overlay={
                          <Popover style={{ maxWidth: 'none' }}>
                            <Popover.Body className="p-2">
                              <div className="d-flex gap-3">
                                <div>
                                  <div className="text-muted small mb-1">Start</div>
                                  <Datetime
                                    input={false}
                                    dateFormat="MM/DD/YYYY"
                                    timeFormat={false}
                                    value={moment(params.start_date)}
                                    onChange={(val) => {
                                      if (moment.isMoment(val)) {
                                        setParams((prev) => ({ ...prev, start_date: val.format('YYYY-MM-DD') }))
                                      }
                                    }}
                                  />
                                </div>
                                <div>
                                  <div className="text-muted small mb-1">End</div>
                                  <Datetime
                                    input={false}
                                    dateFormat="MM/DD/YYYY"
                                    timeFormat={false}
                                    value={moment(params.end_date)}
                                    onChange={(val) => {
                                      if (moment.isMoment(val)) {
                                        setParams((prev) => ({ ...prev, end_date: val.format('YYYY-MM-DD') }))
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                            </Popover.Body>
                          </Popover>
                        }
                      >
                        <Button
                          size="sm"
                          variant={isActive ? 'outline-primary' : 'light'}
                          style={isActive ? { fontWeight: 500 } : undefined}
                          onClick={() => applyPreset(preset)}
                        >
                          {isActive
                            ? `${moment(params.start_date).format('MMM D')} – ${moment(params.end_date).format('MMM D')}`
                            : preset.label}
                        </Button>
                      </OverlayTrigger>
                    )
                  }
                  return (
                    <Button
                      key={preset.key}
                      size="sm"
                      variant={isActive ? 'outline-primary' : 'light'}
                      style={isActive ? { fontWeight: 500 } : undefined}
                      onClick={() => applyPreset(preset)}
                    >
                      {preset.label}
                    </Button>
                  )
                })}
              </div>
                {sectionsLoaded && datasetOptions.length > 1 && (
                  <Button
                    size="sm"
                    variant="light"
                    aria-label="Change dataset"
                    onClick={() => setDatasetModalShow(true)}
                  >
                    <FontAwesomeIcon icon={faLayerGroup} />{' '}
                    {selectedSection?.unit || selectedSection?.name || 'Dataset'}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="light"
                  className="monitoring-settings-toggle"
                  aria-label="Monitoring settings"
                  onClick={() => setConfigureModalShow(true)}
                >
                  <FontAwesomeIcon icon={faCog} /> Configure
                </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {isError && (
        <div className="text-danger">Failed to load monitoring summary.</div>
      )}

      {isLoading && (
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
        </div>
      )}

      {data && !isLoading && (
        <Row xs={1} sm={2} md={statColumns} className="g-3 mb-4">
          <Col>
            <StatCard
              label="Open Findings"
              value={formatNumber(data.open_findings)}
              subtext="waiting for review"
              variant="danger"
            />
          </Col>
          {usesRisk && (
            <Col>
              <StatCard
                label="High-Risk Transactions"
                value={formatNumber(data.high_risk_transactions)}
                subtext={`Risk score ≥ ${params.risk_threshold}`}
                variant="warning"
              />
            </Col>
          )}
          <Col>
            <StatCard
              label="Resolved"
              value={formatNumber(data.resolved_findings)}
              subtext="confirmed and dealt with"
              variant="success"
            />
          </Col>
          <Col>
            <StatCard
              label="Ignored"
              value={formatNumber(data.ignored_findings)}
              subtext="dismissed as false positives"
            />
          </Col>
          <Col>
            <StatCard
              label={isCatchAll ? `Total ${selectedSection?.unit || 'Transactions'}` : 'Total Findings'}
              value={formatNumber(data.total_transactions)}
              subtext={isCatchAll ? 'in selected period' : 'in this review'}
              variant="primary"
            />
          </Col>
        </Row>
      )}

      <div className="d-flex align-items-center gap-4 mt-4 mb-3 flex-wrap">
        {usesRisk && (
          <div className="d-flex align-items-center gap-1">
            <span className="text-muted small fw-bold text-uppercase me-1">Type</span>
            {typeFilters.map((f) => (
              <Button
                key={f.key}
                size="sm"
                variant={activeFilter === f.key ? 'outline-primary' : 'outline-light'}
                className={`bg-white ${activeFilter === f.key ? 'text-primary' : 'text-dark'}`}
                style={activeFilter === f.key ? { fontWeight: 500 } : undefined}
                onClick={() => setActiveFilter(f.key)}
              >
                {f.label}
              </Button>
            ))}
          </div>
        )}

        <div className="d-flex align-items-center gap-1">
          <span className="text-muted small fw-bold text-uppercase me-1">
            Review status
          </span>
          <ButtonGroup size="sm" role="tablist" aria-label="Review status">
            {REVIEW_STATUS_OPTIONS.map((status) => {
              const isAll = status === 'All'
              const selected = isAll
                ? isAllReviewStatuses
                : !isAllReviewStatuses && (params.review_statuses || []).includes(status)
              return (
                <Button
                  key={status}
                  role="tab"
                  aria-selected={selected}
                  variant={selected ? 'primary' : 'light'}
                  className={selected ? undefined : 'bg-white text-dark border'}
                  onClick={() => selectReviewStatus(status)}
                >
                  {status}
                </Button>
              )
            })}
          </ButtonGroup>
        </div>

        <div className="d-flex align-items-center gap-2 ms-auto">
          <span className="text-muted small fw-bold text-uppercase">Sort by</span>
          <Dropdown>
            <Dropdown.Toggle size="sm" variant="outline-light" className="bg-white text-dark">
              {sortBy === 'date' ? (selectedSection?.date_field || 'Date') : SORT_OPTIONS.find((o) => o.key === sortBy)?.label}{' '}
              {sortDir === 'desc' ? '\u2193' : '\u2191'}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {sortOptions.map((opt) => {
                const label = opt.key === 'date'
                  ? (selectedSection?.date_field || 'Date')
                  : opt.label
                return (
                  <React.Fragment key={opt.key}>
                    <Dropdown.Item
                      onClick={() => { setSortBy(opt.key); setSortDir('desc') }}
                    >
                      {label} ↓
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => { setSortBy(opt.key); setSortDir('asc') }}
                    >
                      {label} ↑
                    </Dropdown.Item>
                  </React.Fragment>
                )
              })}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {isLoadingVisible && (
        <div className="text-center my-4">
          <Spinner animation="border" size="sm" variant="primary" />
        </div>
      )}
      {!isLoadingVisible && (
        <div style={{ position: 'relative' }}>
          {isRefetching && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(255, 255, 255, 0.6)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                paddingTop: '2rem'
              }}
            >
              <Spinner animation="border" size="sm" variant="primary" />
            </div>
          )}
          {visibleTransactions.length === 0 && (
            <Card className="shadow-sm">
              <Card.Body className="text-center text-muted py-4">
                No transactions match the current filters.
              </Card.Body>
            </Card>
          )}
          {visibleTransactions.map((transaction) => {
            // Vendor-record findings have no amount or date, so they get their own card.
            // A record carrying both leads with the duplicate.
            if (transaction.duplicate) {
              return (
                <DuplicateFindingCard
                  key={transaction.id}
                  transaction={transaction}
                  onOpenReview={openReview}
                />
              )
            }
            if (transaction.evidence) {
              return (
                <EvidenceFindingCard
                  key={transaction.id}
                  transaction={transaction}
                  onOpenReview={openReview}
                />
              )
            }
            return (
              <TransactionCard
                key={transaction.id}
                transaction={transaction}
                dateLabel={selectedSection?.date_field || 'Date'}
                onSelect={openDrawer}
                onOpenReview={openReview}
              />
            )
          })}

          {visibleTransactions.length > 0 && (
            <div className="mt-3">
              <TableFooter
                meta={{ pages: totalPages, total: totalCount, title: 'findings' }}
                params={pagination}
                setParams={setPagination}
              />
            </div>
          )}
        </div>
      )}

      <ConfigureModal
        show={configureModalShow}
        onHide={() => setConfigureModalShow(false)}
        sectionId={params.section_id}
        showRiskScore={usesRisk}
        riskThreshold={riskThresholdDraft}
        onRiskThresholdChange={setRiskThresholdDraft}
      />

      <Modal
        show={datasetModalShow}
        onHide={() => setDatasetModalShow(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Select dataset</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {Object.entries(groupBy(datasetOptions, 'parent_module')).map(([parentModule, childSections]) => (
            <div key={parentModule} className="mb-4">
              <h5 className="mb-3 fw-bold">{parentModule}</h5>
              <Row>
                {childSections.map((s) => (
                  <Col sm={6} key={s.id} className="mb-2">
                    <ModuleIconCard
                      section={s}
                      params={{ section_id: params.section_id }}
                      setParams={(next) => {
                        setParams((prev) => ({
                          ...prev,
                          section_id: next.section_id
                        }))
                        setDatasetModalShow(false)
                      }}
                    />
                  </Col>
                ))}
              </Row>
            </div>
          ))}
        </Modal.Body>
      </Modal>

      {selectedTransactionId && (() => {
        const selectedTx = visibleTransactions.find((t) => t.id === selectedTransactionId)
        const monitoringContext = selectedTx
          ? {
              sectionId: selectedTx.section_id,
              sectionAbbreviation: selectedTx.section_abbreviation,
              vendorId: selectedTx.vendor_id,
              vendorName: selectedTx.vendor_name,
              employeeId: selectedTx.employee_id,
              employeeName: selectedTx.employee_full_name,
              controlAnalytics: (selectedTx.analytics || []).map((a) => ({
                id: a.id,
                name: a.name
              })),
              highRisk: (selectedTx.groups || []).includes('High Risk')
            }
          : null
        return (
          <QuickLookDrawer
            transactions={visibleTransactions}
            transactionId={selectedTransactionId}
            show={drawerShow}
            setShow={setDrawerShow}
            monitoringReview={selectedTx?.review || null}
            monitoringContext={monitoringContext}
          />
        )
      })()}

      {activeReview && (() => {
        const Surface = activeReview.surface === 'evidence' ? EvidenceReviewModal : DuplicateReviewModal
        return (
          <Surface
            key={activeReview.transaction.id}
            transaction={activeReview.transaction}
            analytic={activeReview.analytic}
            review={activeReview.transaction.review || null}
            onClose={() => setActiveReview(null)}
            onDecided={onReviewDecided}
          />
        )
      })()}
    </Page>
  )
}

export default Monitoring
